import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Clock, AlertCircle, CheckCircle2, CreditCard, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SubscriptionStatus = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
    const interval = setInterval(fetchTrialCountdown, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/subscriptions/current`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSubscription(response.data.subscription);
      
      // If trial, fetch countdown
      if (response.data.subscription?.is_trial) {
        fetchTrialCountdown();
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialCountdown = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/subscriptions/trial/countdown`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.has_trial && !response.data.expired) {
        setCountdown(response.data);
      } else if (response.data.expired) {
        setCountdown({ expired: true });
      }
    } catch (error) {
      console.error('Failed to fetch countdown:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'trial':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'expired':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'trial':
        return <Clock className="w-5 h-5" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card className="border-amber-200">
        <CardHeader className="bg-amber-50">
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <AlertCircle className="w-5 h-5" />
            No Active Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <p className="text-gray-600 mb-4">
            Start your journey with a free 3-day trial or choose a paid plan.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/trial-activation')} variant="outline">
              Start Free Trial
            </Button>
            <Button onClick={() => navigate('/subscriptions')}>
              View Plans
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isExpired = subscription.status === 'expired';
  const isTrial = subscription.is_trial && subscription.status === 'trial';

  return (
    <Card className={`border-2 ${getStatusColor(subscription.status)}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon(subscription.status)}
            <span>Subscription Status</span>
          </div>
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${getStatusColor(subscription.status)}`}>
            {isTrial ? 'Trial' : subscription.status?.toUpperCase()}
          </span>
        </CardTitle>
        <CardDescription>
          {isTrial ? '3-Day Free Trial' : `${subscription.plan_type?.toUpperCase()} Plan`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isTrial && countdown && !countdown.expired && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <p className="font-medium text-blue-900">Trial Ends In:</p>
            </div>
            <div className="flex gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{countdown.days}</div>
                <div className="text-xs text-blue-700">Days</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{countdown.hours}</div>
                <div className="text-xs text-blue-700">Hours</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">{countdown.minutes}</div>
                <div className="text-xs text-blue-700">Minutes</div>
              </div>
            </div>
            <p className="text-sm text-blue-700 mt-3">
              {countdown.message}
            </p>
          </div>
        )}

        {isTrial && countdown?.expired && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="font-medium text-red-900 mb-2">Trial Expired</p>
            <p className="text-sm text-red-700">
              Your trial has ended. Subscribe now to continue using AutomateSEO.ai
            </p>
          </div>
        )}

        <div className="space-y-3 mb-4">
          {!isTrial && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Plan:</span>
              <span className="font-medium capitalize">{subscription.plan_type}</span>
            </div>
          )}
          {!isTrial && subscription.billing_cycle && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Billing Cycle:</span>
              <span className="font-medium capitalize">{subscription.billing_cycle}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Start Date:</span>
            <span className="font-medium">{formatDate(subscription.current_period_start)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">End Date:</span>
            <span className="font-medium">{formatDate(subscription.current_period_end)}</span>
          </div>
          {!isTrial && subscription.amount > 0 && (
            <div className="flex justify-between text-sm pt-2 border-t">
              <span className="text-gray-600">Amount:</span>
              <span className="font-bold">₹{subscription.total_amount?.toLocaleString()}</span>
            </div>
          )}
        </div>

        {isTrial && !countdown?.expired && (
          <Button onClick={() => navigate('/subscriptions')} className="w-full">
            <TrendingUp className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>
        )}

        {(isExpired || countdown?.expired) && (
          <Button onClick={() => navigate('/subscriptions')} className="w-full" variant="destructive">
            <CreditCard className="w-4 h-4 mr-2" />
            Renew Subscription
          </Button>
        )}

        {subscription.status === 'active' && !isTrial && (
          <div className="flex gap-2">
            <Button onClick={() => navigate('/subscriptions')} variant="outline" className="flex-1">
              Upgrade Plan
            </Button>
            <Button onClick={() => navigate('/invoices')} variant="outline" className="flex-1">
              View Invoices
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus;
